import { Pagination, Spin } from 'antd'
import {
  EXPLORER_BORDER_GRAY_LOW_OPACITY,
  useVersionAwareDrilldownPanelData,
} from 'features/drilldown'
import { EntryPropertiesView } from 'features/drilldown/components/ontology/EntryPropertiesView'
import { usePaginatedEntries } from 'features/drilldown/rolodex/dataHelpers/usePaginatedEntries'
import { GenericPanelData } from 'features/drilldown/types'
import { OntologyRolodexEntryType } from 'features/dynamicOntology'
import { FC, useEffect } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  min-height: 120px;
  box-sizing: border-box;
  padding: 1rem;
  overflow-y: auto;
  overflow-x: hidden;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`

const CardContainer = styled.div`
  border: 1px solid ${EXPLORER_BORDER_GRAY_LOW_OPACITY};
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`

const CardHeader = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1890ff;
  font-size: 14px;
`

const EmptyState = styled.div`
  color: #8c8c8c;
  font-style: italic;
  text-align: center;
  padding: 2rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`

export interface CardsPanelData extends GenericPanelData {
  selectedEntryType: OntologyRolodexEntryType
  numberOfEntriesPerPage: number
  currentPage: number
}

export const LATEST_CARDS_PANEL_DATA_SCHEMA_VERSION = 1

export const DEFAULT_CARDS_PANEL_DATA: CardsPanelData = {
  panelDataSchemaVersion: LATEST_CARDS_PANEL_DATA_SCHEMA_VERSION,
  title: 'Cards View',
  isPinned: true,
  isConfigOpen: true,
  // Note that we should adjust this to use the default entry type from the ontology once that is added
  selectedEntryType: RolodexEntryType.UNIT as OntologyRolodexEntryType,
  numberOfEntriesPerPage: 10,
  currentPage: 1,
}

export const CardsPanel: FC = () => {
  const {
    panelData: { selectedEntryType, numberOfEntriesPerPage, currentPage },
    setPanelData,
  } = useVersionAwareDrilldownPanelData<CardsPanelData>()

  const { entries, totalEntries, totalPages, isLoading } = usePaginatedEntries({
    selectedEntryType,
    numberOfEntriesPerPage,
    currentPage,
  })

  const entriesOfRelevantType = entries.filter(
    (entry) => entry.type === selectedEntryType
  )

  // Handle current page being > than total pages: reset current to 1
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setPanelData((prev) => ({
        ...prev,
        currentPage: 1,
      }))
    }
  }, [currentPage, totalPages, setPanelData])

  const handlePageSizeChange = (current: number, size: number) => {
    setPanelData((prev) => ({
      ...prev,
      numberOfEntriesPerPage: size,
      currentPage: 1,
    }))
  }

  const handlePageChange = (page: number) => {
    setPanelData((prev) => ({
      ...prev,
      currentPage: page,
    }))
  }

  return (
    <PanelContainer>
      {isLoading ? (
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      ) : entries.length === 0 ? (
        <EmptyState>
          No entries found for the selected type and filters.
        </EmptyState>
      ) : (
        <>
          <CardsGrid>
            {entriesOfRelevantType.map((entry) => (
              <CardContainer key={entry.id}>
                <CardHeader>{entry.id}</CardHeader>
                <EntryPropertiesView
                  entryData={entry.data}
                  maxWidth='100%'
                  isCard={true}
                />
              </CardContainer>
            ))}
          </CardsGrid>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem',
              width: '100%',
            }}>
            <Pagination
              current={currentPage}
              pageSize={numberOfEntriesPerPage}
              total={totalEntries}
              showSizeChanger={true}
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} entries`
              }
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </PanelContainer>
  )
}
